import * as THREE from 'three';

type SlideParticle = {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  life: number;
  maxLife: number;
};

/** Ground dust puffs while the player is sliding. */
export class FpsSlideVfx {
  private readonly particles: SlideParticle[] = [];
  private spawnAcc = 0;
  private readonly geo = new THREE.SphereGeometry(0.045, 4, 4);
  private readonly mat = new THREE.MeshBasicMaterial({
    color: 0xd4c8ac,
    transparent: true,
    opacity: 0.72,
    depthWrite: false,
  });
  private readonly billboardGeo = new THREE.PlaneGeometry(0.11, 0.11);
  private readonly billboardMat = new THREE.MeshBasicMaterial({
    color: 0xe8dcc8,
    transparent: true,
    opacity: 0.62,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  constructor(private readonly scene: THREE.Scene) {}

  update(
    dt: number,
    sliding: boolean,
    feetX: number,
    feetY: number,
    feetZ: number,
    horizSpeed: number,
    camera?: THREE.Camera,
  ): void {
    if (sliding && horizSpeed > 1.2) {
      this.spawnAcc += dt;
      const interval = horizSpeed > 8 ? 0.028 : 0.042;
      while (this.spawnAcc >= interval) {
        this.spawnAcc -= interval;
        this.spawnBurst(feetX, feetY, feetZ, horizSpeed, camera);
      }
    } else {
      this.spawnAcc = 0;
    }

    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i]!;
      p.life += dt;
      p.mesh.position.addScaledVector(p.vel, dt);
      p.vel.y -= dt * 3.2;
      p.vel.multiplyScalar(1 - dt * 2.4);
      const t = p.life / p.maxLife;
      const mat = p.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = (1 - t) * (p.mesh.geometry === this.billboardGeo ? 0.62 : 0.72);
      p.mesh.scale.setScalar(0.85 + t * 1.15);
      if (camera && p.mesh.geometry === this.billboardGeo) {
        p.mesh.lookAt(camera.position);
      }
      if (p.life >= p.maxLife) {
        this.scene.remove(p.mesh);
        if (p.mesh.material !== this.mat && p.mesh.material !== this.billboardMat) {
          (p.mesh.material as THREE.Material).dispose();
        }
        this.particles.splice(i, 1);
      }
    }
  }

  private spawnBurst(
    x: number,
    y: number,
    z: number,
    speed: number,
    camera?: THREE.Camera,
  ): void {
    const count = speed > 9 ? 3 : 2;
    for (let i = 0; i < count; i++) {
      const useBillboard = i === 0 && camera;
      const mesh = new THREE.Mesh(
        useBillboard ? this.billboardGeo : this.geo,
        useBillboard ? this.billboardMat.clone() : this.mat.clone(),
      );
      mesh.position.set(
        x + (Math.random() - 0.5) * 0.28,
        y + 0.04 + Math.random() * 0.06,
        z + (Math.random() - 0.5) * 0.28,
      );
      if (useBillboard) mesh.lookAt(camera!.position);
      this.scene.add(mesh);
      const angle = Math.random() * Math.PI * 2;
      const spread = 0.5 + Math.random() * (0.6 + speed * 0.04);
      this.particles.push({
        mesh,
        vel: new THREE.Vector3(
          Math.cos(angle) * spread,
          0.35 + Math.random() * 0.55,
          Math.sin(angle) * spread,
        ),
        life: 0,
        maxLife: 0.18 + Math.random() * 0.16,
      });
    }
  }

  dispose(): void {
    for (const p of this.particles) {
      this.scene.remove(p.mesh);
      if (p.mesh.material !== this.mat && p.mesh.material !== this.billboardMat) {
        (p.mesh.material as THREE.Material).dispose();
      }
    }
    this.particles.length = 0;
    this.geo.dispose();
    this.mat.dispose();
    this.billboardGeo.dispose();
    this.billboardMat.dispose();
  }
}
